import PhoneShowcase from "./PhoneShowcase";

/**
 * GovAuthShowcase — versão da vertical Gestão Pública do AuthShowcase.
 *
 * Dark cinematic stage à esquerda da tela de login `/gov/login`, com
 * cenários de WhatsApp cidadão × Prefeitura Municipal e copy alinhada
 * ao site institucional público (agentus.com.br/gov).
 *
 * Escondido no mobile — mantém o form respirando no fundo preto.
 */

// 3 cenários realistas de atendimento cidadão × órgão público.
// Bolhas verdes = cidadão / bolhas brancas = AgentUS respondendo pela prefeitura
// (com dados da Base de Conhecimento Institucional).
const GOV_SCENARIOS = [
  [
    { from: "client", text: "Boa tarde! Como faço pra emitir a 2ª via do IPTU deste ano?", time: "14:07" },
    { from: "ai", text: "Boa tarde, Sr. Carlos! 👋 Pela nossa Base Institucional, a 2ª via pode ser emitida direto no portal https://prefeitura.gov.br/iptu — precisa do CPF ou inscrição do imóvel.", time: "14:07" },
    { from: "client", text: "E se eu quiser parcelar em atraso, tem desconto?", time: "14:08" },
    { from: "ai", text: "Sim! Pelo Decreto Municipal 4.812/2025 há 30% de desconto em pagamento à vista de débitos até 2024. Parcelamento em até 12x sem desconto. Quer que eu te oriente pela Secretaria da Fazenda?", time: "14:08" },
  ],
  [
    { from: "client", text: "Oi, preciso agendar vacina de rotina do meu filho de 1 ano 💉", time: "10:22" },
    { from: "ai", text: "Claro! Pela Base da Secretaria de Saúde, aos 12 meses seu filho toma Tríplice Viral, Meningo C e Pneumo 10. Qual UBS fica mais perto de você?", time: "10:22" },
    { from: "client", text: "A do Jardim Aurora", time: "10:23" },
    { from: "ai", text: "UBS Jardim Aurora atende vacinação de seg-sex, 8h às 16h. Tenho 3 horários amanhã: 9h · 10h30 · 14h. Qual prefere?", time: "10:23" },
  ],
  [
    { from: "client", text: "O poste da minha rua tá apagado faz 5 dias! Rua das Palmeiras, 123.", time: "18:44" },
    { from: "ai", text: "Sinto muito pelo transtorno, Sra. Ana. Vou abrir um protocolo agora com a Secretaria de Obras.", time: "18:44" },
    { from: "ai", text: "Protocolo #2026-04812 aberto ✅ Prazo previsto pelo Manual de Serviços Urbanos: 72h úteis. Você recebe um WhatsApp quando o reparo for concluído.", time: "18:45" },
    { from: "client", text: "Uau, que rápido! Obrigada 🙏", time: "18:45" },
  ],
];

export default function GovAuthShowcase() {
  return (
    <aside
      data-testid="gov-auth-showcase"
      className="hidden lg:flex relative basis-1/2 grow-0 shrink-0 min-h-screen flex-col text-white overflow-hidden"
      style={{ backgroundColor: "#04060B" }}
    >
      {/* Glow institucional — verde discreto + azul frio (não bombado) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(560px 640px at 50% 58%, rgba(52,168,83,0.09), transparent 62%), radial-gradient(760px 560px at 50% 46%, rgba(96,140,255,0.08), transparent 65%), radial-gradient(900px 500px at 50% 110%, rgba(255,255,255,0.03), transparent 70%)",
        }}
      />
      {/* Grid pontilhado sutil */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
          backgroundSize: "34px 34px",
        }}
      />
      {/* Fade inferior */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-black/70 to-transparent"
      />

      {/* TOPO — logomarca real + badge institucional */}
      <div className="relative z-10 flex items-center justify-between px-10 xl:px-14 pt-8">
        <img
          src="/logo-white.png"
          alt="AgentUS Gestão Pública"
          className="h-[18px] w-auto opacity-95 select-none"
          draggable="false"
          data-testid="gov-showcase-logo"
        />
        <span className="inline-flex items-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.03] px-3 py-1.5">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
            <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-[9.5px] uppercase tracking-[0.22em] text-white/55 font-medium select-none">
            Gestão Pública
          </span>
        </span>
      </div>

      {/* CENTRO — headline + phone live */}
      <div className="relative z-10 flex-1 min-h-0 flex flex-col items-center justify-center px-10 py-6 gap-6">
        <div className="text-center">
          <div className="text-[9.5px] uppercase tracking-[0.32em] text-white/35 font-medium mb-3 select-none">
            Conhecimento oficial. Sempre disponível.
          </div>
          <h2
            className="font-heading text-[26px] xl:text-[30px] font-semibold leading-[1.12] tracking-[-0.025em]"
            style={{ textWrap: "balance" }}
          >
            Isso não é um chatbot.
            <br />
            <span className="text-white/50">É o seu município, ao vivo no WhatsApp.</span>
          </h2>
        </div>

        <PhoneShowcase
          scenarios={GOV_SCENARIOS}
          contactName="Prefeitura Municipal"
          contactInitials="PM"
        />
      </div>

      {/* RODAPÉ — pilares institucionais */}
      <div className="relative z-10 px-10 xl:px-14 pb-7">
        <div className="flex items-center justify-center gap-8 mb-4">
          <Pillar label="WhatsApp 24/7" />
          <Pillar label="Base institucional" />
          <Pillar label="LGPD compliant" />
        </div>
        <p className="text-[9.5px] tracking-[0.18em] uppercase text-white/22 text-center">
          Multi-tenant · Auditável · Ambiente isolado por órgão
        </p>
      </div>
    </aside>
  );
}

function Pillar({ label }) {
  return (
    <div className="flex items-center gap-2 text-[11px] text-white/50 tracking-tight">
      <span className="w-4 h-px bg-white/25" />
      {label}
    </div>
  );
}
